/*
  rd-home.js - page behaviour for the two homepage redesign prototypes (layout/redesign.liquid).
  Everything here is progressive: with JavaScript off the page reads top to bottom, every step is
  visible, every link works. Reduced motion shows every end state immediately.

    [data-rd-reveal]        fades/rises in once when it enters the viewport
    [data-rd-pin]           a tall section whose sticky inner scrubs through [data-rd-step] children
    [data-rd-count]         a number that counts up to its value once, on reveal
    [data-rd-flavour-btn]   flavour picker: tells the 3D layer (rd:flavour) and retargets the CTA
    [data-rd-clip]          a muted video that only plays while it is on screen
    [data-rd-bar]           the sticky shop bar, shown after the hero and before the footer
    [data-rd-phase]         Direction B: the page colour, ink and clock follow the time of day
    [data-rd-reviews]       live Judge.me review cards, screened like home26-reviews
*/
(function () {
  'use strict';
  var doc = document, root = doc.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var $$ = function (s, c) { return [].slice.call((c || doc).querySelectorAll(s)); };
  var clamp = function (v) { return Math.min(1, Math.max(0, v)); };
  root.classList.add('rd-js');
  if (reduce) { root.classList.add('rd-reduce'); }

  /* ---------- reveal + count-up ---------- */
  function countUp(el) {
    var to = parseFloat(el.getAttribute('data-rd-count')), dec = (el.getAttribute('data-rd-count').split('.')[1] || '').length;
    if (reduce || !isFinite(to)) { el.textContent = el.getAttribute('data-rd-count'); return; }
    var t0 = null, dur = 1100;
    function step(t) {
      if (!t0) { t0 = t; }
      var k = clamp((t - t0) / dur), e = 1 - Math.pow(1 - k, 4);
      el.textContent = (to * e).toFixed(dec);
      if (k < 1) { requestAnimationFrame(step); }
    }
    requestAnimationFrame(step);
  }
  var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) {
      if (!en.isIntersecting) { return; }
      en.target.classList.add('is-in');
      $$('[data-rd-count]', en.target).forEach(countUp);
      if (en.target.hasAttribute('data-rd-count')) { countUp(en.target); }
      io.unobserve(en.target);
    });
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }) : null;
  $$('[data-rd-reveal]').forEach(function (el) { if (io && !reduce) { io.observe(el); } else { el.classList.add('is-in'); } });
  // [data-rd-inview]: the live sections' own entrance (e.g. the collage in "Bring your water to life")
  $$('[data-rd-inview]').forEach(function (el) {
    if (!('IntersectionObserver' in window) || reduce) { el.classList.add('is-in'); return; }
    var o = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { el.classList.add('is-in'); o.disconnect(); } }, { threshold: 0.3 });
    o.observe(el);
  });

  /* ---------- pinned step sequences ---------- */
  var pins = $$('[data-rd-pin]').map(function (el) { return { el: el, steps: $$('[data-rd-step]', el), last: -1 }; });
  function updatePins() {
    var vh = window.innerHeight;
    pins.forEach(function (p) {
      var r = p.el.getBoundingClientRect(), span = r.height - vh;
      var prog = clamp(span > 0 ? -r.top / span : 0);
      p.el.style.setProperty('--p', prog.toFixed(4));
      if (!p.steps.length) { return; }
      var i = Math.min(p.steps.length - 1, Math.floor(prog * p.steps.length * 0.999));
      if (i !== p.last) {
        p.steps.forEach(function (s, j) { s.classList.toggle('is-on', j === i); s.classList.toggle('is-past', j < i); });
        p.last = i;
      }
    });
  }

  /* ---------- Direction B: the day ---------- */
  var phases = $$('[data-rd-phase]');
  var clockEl = doc.querySelector('[data-rd-clock]');
  function hex(h) { h = h.replace('#', ''); return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]; }
  function mix(a, b, t) { return 'rgb(' + a.map(function (v, i) { return Math.round(v + (b[i] - v) * t); }).join(',') + ')'; }
  var lastPhase = null;
  function updatePhases() {
    if (!phases.length) { return; }
    var mid = window.innerHeight * 0.55, cur = phases[0], next = null, t = 0;
    for (var i = 0; i < phases.length; i++) {
      var r = phases[i].getBoundingClientRect();
      if (r.top <= mid) { cur = phases[i]; next = phases[i + 1] || null; t = next ? clamp((mid - r.bottom + window.innerHeight * 0.35) / (window.innerHeight * 0.35)) : 0; }
    }
    var a = hex(cur.getAttribute('data-rd-phase')), b = next ? hex(next.getAttribute('data-rd-phase')) : a;
    root.style.setProperty('--rd-bg', mix(a, b, reduce ? Math.round(t) : t));
    var dark = (t > 0.5 && next ? next : cur).getAttribute('data-rd-ink') === 'light';
    root.classList.toggle('rd-dark', dark);
    var owner = t > 0.5 && next ? next : cur;
    if (owner !== lastPhase) {
      lastPhase = owner;
      if (clockEl) { clockEl.textContent = owner.getAttribute('data-rd-time') || nowStr(); clockEl.classList.remove('is-tick'); void clockEl.offsetWidth; clockEl.classList.add('is-tick'); }
      var label = doc.querySelector('[data-rd-clock-label]');
      if (label) { label.textContent = owner.getAttribute('data-rd-label') || ''; }
      doc.dispatchEvent(new CustomEvent('rd:theme', { detail: {
        key: owner.getAttribute('data-rd-key') || '#ffffff',
        exposure: parseFloat(owner.getAttribute('data-rd-exposure') || '1.05'),
        air: owner.getAttribute('data-rd-air') || null,
        fizz: owner.getAttribute('data-rd-fizz') || null,
      } }));
    }
  }
  // The hero has no set time: it shows the visitor's own.
  function nowStr() { var d = new Date(); return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2); }
  if (clockEl) {
    setInterval(function () { if (lastPhase && !lastPhase.getAttribute('data-rd-time')) { clockEl.textContent = nowStr(); } }, 15000);
  }

  /* ---------- sticky shop bar ---------- */
  var bar = doc.querySelector('[data-rd-bar]'), hero = doc.querySelector('[data-rd-hero]') || doc.querySelector('main section'), foot = doc.querySelector('[data-rd-foot]');
  function updateBar() {
    if (!bar || !hero) { return; }
    var past = hero.getBoundingClientRect().bottom < 80;
    var atFoot = foot && foot.getBoundingClientRect().top < window.innerHeight;
    bar.classList.toggle('is-shown', past && !atFoot);
  }

  /* ---------- one scroll handler for all of the above ---------- */
  var queued = false;
  function onScroll() {
    if (queued) { return; }
    queued = true;
    requestAnimationFrame(function () { queued = false; updatePins(); updatePhases(); updateBar(); });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();

  /* ---------- flavour picker ---------- */
  var FL = { berry: 'Berry', lemon: 'Lemon', tropical: 'Tropical' };
  $$('[data-rd-flavours]').forEach(function (group) {
    var btns = $$('[data-rd-flavour-btn]', group), cta = group.querySelector('[data-rd-flavour-cta]');
    btns.forEach(function (b) {
      b.addEventListener('click', function () {
        var f = b.getAttribute('data-rd-flavour-btn');
        btns.forEach(function (o) { o.setAttribute('aria-pressed', o === b ? 'true' : 'false'); });
        group.setAttribute('data-flavour', f);
        if (cta) {
          cta.href = '/products/base-250?flavour=' + f;
          var lbl = cta.querySelector('[data-rd-flavour-name]'); if (lbl) { lbl.textContent = FL[f]; }
        }
        doc.dispatchEvent(new CustomEvent('rd:flavour', { detail: f }));
      });
    });
  });

  /* ---------- clips: play only while on screen ---------- */
  var clips = $$('video[data-rd-clip]');
  if (clips.length && 'IntersectionObserver' in window && !reduce) {
    var vio = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var v = en.target;
        if (en.isIntersecting && en.intersectionRatio > 0.5) {
          if (v.preload === 'none') { v.preload = 'metadata'; }
          var pr = v.play(); if (pr && pr.catch) { pr.catch(function () {}); }
        } else { v.pause(); }
      });
    }, { threshold: [0, 0.5, 0.75] });
    clips.forEach(function (v) { vio.observe(v); });
  }
  $$('[data-rd-rail]').forEach(function (rail) {
    var track = rail.querySelector('[data-rd-track]');
    $$('[data-rd-rail-go]', rail).forEach(function (b) {
      b.addEventListener('click', function () {
        track.scrollBy({ left: (+b.getAttribute('data-rd-rail-go')) * track.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
      });
    });
  });

  /* ---------- live reviews (Judge.me block on the nasal strips product) ---------- */
  var tpl = doc.querySelector('template[data-rd-jdgm]'), revRail = doc.querySelector('[data-rd-reviews]');
  if (tpl && revRail && tpl.content) {
    // Same screen as home26-reviews: no condition or treatment language in a homepage testimonial.
    var SCREEN = /snor|sleep|night|apn(o)?ea|congest|block|sinus|cold\b|flu\b|allerg|asthma|septum|cure|treat|fixed|heal|medical|doctor/i;
    var esc = function (t) { var d = doc.createElement('div'); d.textContent = t; return d.innerHTML; };
    var shortName = function (n) {
      n = (n || '').trim();
      if (!n || /^anonymous$/i.test(n)) { return 'Verified buyer'; }
      var p = n.split(/\s+/), first = p[0].charAt(0).toUpperCase() + p[0].slice(1);
      return p.length > 1 ? first + ' ' + p[p.length - 1].charAt(0).toUpperCase() + '.' : first;
    };
    var star = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 1.6l2.6 5.3 5.8.8-4.2 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.6 7.7l5.8-.8z"/></svg>';
    var cards = [];
    $$('.jdgm-rev', tpl.content).forEach(function (r) {
      var sc = r.querySelector('[data-score]'), score = sc ? +sc.getAttribute('data-score') : 0;
      var title = ((r.querySelector('.jdgm-rev__title') || {}).textContent || '').trim();
      var body = ((r.querySelector('.jdgm-rev__body') || {}).textContent || '').replace(/\s+/g, ' ').trim();
      if (score < 4 || body.length < 25 || SCREEN.test(title + ' ' + body)) { return; }
      var name = shortName((r.querySelector('.jdgm-rev__author') || {}).textContent);
      var verified = r.getAttribute('data-verified-buyer') === 'true';
      cards.push('<article class="rd-rev" data-rd-reveal><div class="rd-rev-stars" role="img" aria-label="' + score + ' out of 5 stars">' + new Array(score + 1).join(star) + '</div>' +
        (title ? '<h3>' + esc(title) + '</h3>' : '') + '<p>' + esc(body) + '</p>' +
        '<span class="rd-rev-who">' + esc(name) + (verified ? ' <i>Verified buyer</i>' : '') + '</span></article>');
    });
    if (cards.length) { revRail.innerHTML = cards.join(''); $$('.rd-rev', revRail).forEach(function (c) { c.classList.add('is-in'); }); }
    else { revRail.hidden = true; }
  }
})();
